<template>
  <div class="content-area">
    <hero-bar>
      Taşıt Görev Talebi
      <b-button
        slot="right"
        icon-left="content-save-off"
        type="is-warning"
        class="mr-3"
        tag="router-link"
        :to="{ name: 'transfers.index' }"
      >
        {{ $t('cancel') }}
      </b-button>
      <b-button
        slot="right"
        icon-left="content-save"
        type="is-link"
        :class="{ 'is-loading': isLoading }"
        @click="submit"
      >
        {{ $t('save') }}
      </b-button>
    </hero-bar>

    <form ref="form" @submit.prevent="submit">
      <section class="section is-main-section">
        <card-component :title="$t('requestForm')" icon="code-greater-than-or-equal">
          <tiles>
            <div class="columns is-centered is-fullwidth">
              <div class="">
                <b-field class="has-text-centered" label="Görev Günü" />
                <b-datetimepicker
                  v-model="form.date"
                  :min-datetime="minDateTime"
                  :timepicker="{ incrementMinutes: 1 }"
                  :datepicker="{
                    monthNames: $t('dateTimePicker.monthNames'),
                    dayNames: $t('dateTimePicker.dayNames'),
                    firstDayOfWeek: 1
                  }"
                  inline
                  size="is-medium"
                />
              </div>
            </div>

            <div class="is-fullwidth">
              <h5 class="is-size-6 has-text-weight-bold mb-3">{{ $t('startAddress') }}</h5>

              <b-field>
                <b-select
                  v-model="form.from_province_id"
                  placeholder="İl Seçiniz"
                  validation-message="Lütfen il seçiniz."
                  required
                  expanded
                  @change.native="form.from_district_id = null"
                >
                  <option
                    v-for="province in provinces"
                    :key="province.id"
                    :value="province.id"
                  >
                    {{ province.name }}
                  </option>
                </b-select>
              </b-field>

              <b-field v-if="isFieldVisible('transfer', 'from_district_id')">
                <b-select
                  v-model="form.from_district_id"
                  placeholder="İlçe Seçiniz"
                  validation-message="Lütfen ilçe seçiniz."
                  :required="rules.from_district_id && rules.from_district_id.includes('required')"
                  expanded
                  @change.native="changeFromDistrict"
                >
                  <option
                    v-for="district in districts(form.from_province_id)"
                    :key="district.id"
                    :value="district.id"
                  >
                    {{ district.name }}
                  </option>
                </b-select>
              </b-field>

              <b-field v-if="isFieldVisible('transfer', 'from_neighborhood_id')">
                <b-select
                  v-model="form.from_neighborhood_id"
                  placeholder="Mahalle Seçiniz"
                  validation-message="Lütfen mahalle seçiniz."
                  :required="rules.from_neighborhood_id && rules.from_neighborhood_id.includes('required')"
                  expanded
                >
                  <option
                    v-for="neighborhood in neighborhoodsFrom"
                    :key="neighborhood.id"
                    :value="neighborhood.id"
                  >
                    {{ neighborhood.name }}
                  </option>
                </b-select>
              </b-field>

              <b-field>
                <b-input
                  v-model="form.from_address_line"
                  maxlength="200"
                  type="textarea"
                  placeholder="Açık Adres"
                />
              </b-field>

              <h5 class="is-size-6 has-text-weight-bold mb-3">Talep Bilgileri</h5>

              <b-field>
                <b-select
                  v-model="form.request_unit"
                  placeholder="Talep Eden Birim Seçiniz"
                  validation-message="Lütfen talep eden birim seçiniz."
                  required
                  expanded
                >
                  <option
                    v-for="requestUnit in requestUnits"
                    :key="requestUnit.id"
                    :value="requestUnit.id"
                  >
                    {{ requestUnit.name }}
                  </option>
                </b-select>
              </b-field>

              <b-field v-if="isFieldVisible('transfer', 'requested_unit_id')">
                <b-select
                  v-model="form.requested_unit_id"
                  placeholder="Talep Edilen Birim Seçiniz"
                  validation-message="Lütfen talep edilen birim seçiniz."
                  :required="rules.requested_unit_id && rules.requested_unit_id.includes('required')"
                  expanded
                >
                  <option
                    v-for="requestUnit in requestUnits"
                    :key="requestUnit.id"
                    :value="requestUnit.id"
                  >
                    {{ requestUnit.name }}
                  </option>
                </b-select>
              </b-field>

              <b-field>
                <b-input
                  v-model="form.demandant_name"
                  type="text"
                  placeholder="Talep Eden Ad & Soyad"
                  name="demandant_name"
                />
              </b-field>

              <b-field>
                <b-input
                  v-model="form.demandant_phone"
                  v-cleave="masks.phone"
                  type="tel"
                  placeholder="Talep Eden Telefon"
                  name="demandant_phone"
                />
              </b-field>
              <b-field>
                <b-select
                  v-model="form.vehicle_type_id"
                  :placeholder="$t('selectVehicleType')"
                  validation-message="Lütfen araç cinsi seçiniz."
                  required
                  expanded
                >
                  <option
                    v-for="vehicleType in vehicleTypes"
                    :key="vehicleType.id"
                    :value="vehicleType.id"
                  >
                    {{ vehicleType.name }}
                  </option>
                </b-select>
              </b-field>

              <b-field v-if="checkPermission('self_drive')">
                <b-checkbox v-model="form.self_drive" :value="false">
                  {{ $t('selfDriveRequest') }}
                </b-checkbox>
              </b-field>

              <div v-if="settings('general.driverCarAndVehicleSelectableWithDriverRole')">
                <h5 class="is-size-6 has-text-weight-bold mb-3">Sürücü ve Araç Bilgileri</h5>
                <b-field>
                  <b-input
                    v-model="form.estimated_time"
                    :placeholder="$t('estimatedTime')"
                    type="number"
                    min="1"
                    step="0.01"
                  />
                </b-field>
                <b-field>
                  <b-autocomplete
                    v-model="driver"
                    :disabled="form.estimated_time < 1 || form.self_drive"
                    field="name"
                    :data="drivers"
                    :placeholder="$t('chooseDriver')"
                    :loading="isDriverFetching"
                    @typing="getAsyncDriverData"
                    @select="selectDriver"
                  >
                    <div slot-scope="props">
                      <div class="media">
                        <div class="media-content">
                          <b>{{ props.option.name }}</b>
                          <br>
                          <small>
                            {{ props.option.phone | phone }}
                          </small>
                        </div>
                      </div>
                    </div>
                  </b-autocomplete>
                </b-field>
                <b-field>
                  <b-autocomplete
                    v-model="secondDriver"
                    :disabled="form.estimated_time < 1 || form.self_drive"
                    field="name"
                    :data="drivers"
                    :placeholder="$t('chooseDriverSecond')"
                    :loading="isDriverSecondFetching"
                    @typing="getAsyncSecondDriverData"
                    @select="selectSecondDriver"
                  >
                    <div slot-scope="props">
                      <div class="media">
                        <div class="media-content">
                          <b>{{ props.option.name }}</b>
                          <br>
                          <small>
                            {{ props.option.phone | phone }}
                          </small>
                        </div>
                      </div>
                    </div>
                  </b-autocomplete>
                </b-field>
                <b-field>
                  <b-autocomplete
                    v-model="form.car_plate"
                    field="plate"
                    class="leading-none"
                    :data="cars"
                    :placeholder="$t('chooseCar')"
                    :loading="isCarFetching"
                    :disabled="form.estimated_time < 1"
                    @typing="getAsyncCarData"
                    @select="selectCar"
                  >
                    <template slot-scope="props">
                      {{ props.option.plate }}
                      <span class="emptyText">
                        ({{ props.option.brand }} - {{ props.option.model }})
                        <br>
                        <small>{{ props.option.vehicle_type ? props.option.vehicle_type.name : '-' }}</small>
                      </span>
                    </template>
                  </b-autocomplete>
                </b-field>
              </div>
            </div>

            <div class="is-fullwidth">
              <h5 class="is-size-6 has-text-weight-bold mb-3">{{ $t('targetAddress') }}</h5>

              <b-field>
                <b-select
                  v-model="form.to_province_id"
                  placeholder="İl Seçiniz"
                  required
                  expanded
                  @change.native="form.to_district_id = null"
                >
                  <option
                    v-for="province in provinces"
                    :key="province.id"
                    :value="province.id"
                  >
                    {{ province.name }}
                  </option>
                </b-select>
              </b-field>

              <b-field v-if="isFieldVisible('transfer', 'to_district_id')">
                <b-select
                  v-model="form.to_district_id"
                  placeholder="İlçe Seçiniz"
                  validation-message="Lütfen ilçe seçiniz."
                  :required="rules.to_district_id && rules.to_district_id.includes('required')"
                  expanded
                  @change.native="changeToDistrict"
                >
                  <option
                    v-for="district in districts(form.to_province_id)"
                    :key="district.id"
                    :value="district.id"
                  >
                    {{ district.name }}
                  </option>
                </b-select>
              </b-field>

              <b-field v-if="isFieldVisible('transfer', 'to_neighborhood_id')">
                <b-select
                  v-model="form.to_neighborhood_id"
                  placeholder="Mahalle Seçiniz"
                  validation-message="Lütfen mahalle seçiniz."
                  :required="rules.to_neighborhood_id && rules.to_neighborhood_id.includes('required')"
                  expanded
                >
                  <option
                    v-for="neighborhood in neighborhoodsTo"
                    :key="neighborhood.id"
                    :value="neighborhood.id"
                  >
                    {{ neighborhood.name }}
                  </option>
                </b-select>
              </b-field>

              <b-field>
                <b-input
                  v-model="form.to_address_line"
                  maxlength="200"
                  type="textarea"
                  placeholder="Açık Adres"
                />
              </b-field>

              <h5 class="is-size-6 has-text-weight-bold mb-3">Görev Türü</h5>

              <b-field>
                <b-select
                  v-model="form.duty_type"
                  placeholder="Görev Türü Seçiniz"
                  validation-message="Lütfen görev türü seçiniz."
                  required
                  expanded
                >
                  <option
                    v-for="dutyType in dutyTypes"
                    :key="dutyType.id"
                    :value="dutyType.id"
                  >
                    {{ dutyType.name }}
                  </option>
                </b-select>
              </b-field>

              <h5 class="is-size-6 has-text-weight-bold mb-3">{{ $t('aciklama') }}</h5>

              <b-field>
                <b-input
                  v-model="form.user_description"
                  maxlength="200"
                  rows="3"
                  type="textarea"
                  :placeholder="$t('aciklama')"
                />
              </b-field>
            </div>
          </tiles>
        </card-component>

        <div class="flex-left">
          <div
            v-for="(passenger, index) in form.passengers"
            :key="index"
            class="passenger_box"
          >
            <card-component
              :title="(index + 1) +'. Kişi'"
              header-icon="close-box"
              @header-icon-click="deletePassenger(index)"
            >
              <b-field :label="$t('fullname')">
                <b-input
                  v-model="form.passengers[index].name"
                  icon="rename-box"
                  maxlength="50"
                  custom-class="additional_passenger_name"
                />
              </b-field>

              <b-field :label="$t('phoneNumber')">
                <b-input
                  v-model="form.passengers[index].phone"
                  v-cleave="masks.phone"
                  type="tel"
                  icon="cellphone"
                  @focus="focusInput"
                />
              </b-field>
            </card-component>
          </div>

          <card-component
            class="add_additional_passenger"
            @click.native="addPassenger"
          >
            <b-tooltip
              label="Kişi Ekle"
              type="is-dark"
            >
              <b-icon icon="account-plus" size="is-large" type="is-light" />
            </b-tooltip>
          </card-component>
        </div>
      </section>
    </form>
  </div>
</template>
<script>
import Cleave from 'cleave.js'
import 'cleave.js/dist/addons/cleave-phone.tr'
import moment from 'moment'
import { mapActions, mapState, mapGetters } from 'vuex'
import debounce from 'lodash/debounce'

export default {
  name: 'TransferRequest',
  components: {
    CardComponent: () => import('@/components/CardComponent'),
    HeroBar: () => import('@/components/HeroBar'),
    Tiles: () => import('@/components/Tiles'),
  },
  title: ({ $i18n }) => $i18n.t('titles.transferRequest'),
  directives: {
    cleave: {
      inserted: (el, binding) => {
        const input = el.querySelector('input')
        input.cleave = new Cleave(input, binding.value || {})
      },
      update: (el) => {
        const input = el.querySelector('input')
        const event = new Event('input', { bubbles: true });
        setTimeout(() => {
          input.value = input.cleave.properties.result
          input.dispatchEvent(event)
        }, 100);
      },
      unbind (el) {
        const input = el.querySelector('input')
        input.cleave.destroy()
      }
    }
  },
  data () {
    return {
      isLoading: false,
      formValid: false,
      form: {
        date: null,
        from_province_id: null,
        from_district_id: null,
        from_neighborhood_id: null,
        from_address_line: null,
        to_province_id: null,
        to_district_id: null,
        to_neighborhood_id: null,
        to_address_line: null,
        demandant_name: '',
        demandant_phone: '',
        duty_type: null,
        request_unit: null,
        vehicle_type_id: null,
        user_description: '',
        passengers: [],
        self_drive: false,
        estimated_time: null,
        driver_id: null,
        second_driver_id: null,
        car_plate: null,
        car_id: null,
        requested_unit_id: null,
      },
      neighborhoodsFrom: [],
      neighborhoodsTo: [],
      drivers: [],
      driver: null,
      secondDriver: null,
      isDriverFetching: false,
      isDriverSecondFetching: false,
      cars: [],
      isCarFetching: false,
      rules: {},
      masks: {
        phone: {
          phone: true,
          phoneRegionCode: 'TR',
          delimiter: ' '
        }
      }
    }
  },
  computed: {
    ...mapState([
      'user',
    ]),
    ...mapGetters({
      settings: 'settings/settings',
      isFieldVisible: 'settings/isFieldVisible',
      provinces: 'provinces/provinces',
      districts: 'provinces/districts',
      getSpecifications: 'getSpecifications',
      vehicleTypes: 'vehicleTypes/all',
    }),
    dutyTypes () {
      return this.getSpecifications(1)
    },
    requestUnits () {
      return this.getSpecifications(2)
    },
    minDateTime () {
      const minute = moment().format('m') % 5
      return moment().subtract(minute, 'minutes').add(15, 'minutes').toDate()
    },
    isVoidable () {
      return [0, 1, 2].includes(parseInt(this.data.status))
    },
  },
  beforeDestroy () {
    this.$store.commit('setAsideActiveMenuKey', '')
  },
  async created () {
    this.$store.commit('setAsideActiveMenuKey', 'transfers.index')

    const transferId = Number(this.$route.query.id)

    // Sıfır ile başlamayan tam sayı ise al.
    if (/^(?!0)\d+$/gmi.test(transferId)) {
      this.loadTransferDetails(transferId)
    }

    this.$store.dispatch('getSpecifications')

    this.fetchVehicleTypes({ is_active: 1, per_page: -1 })

    if (this.settings('transfer').length === 0) {
      await this.fetchSettings()
    }

    if (this.provinces.length === 0) {
      await this.fetchProvinces()
    }

    const defaults = this.settings('transfer.defaults')

    this.form.from_province_id = defaults.from_province_id
    this.form.from_district_id = defaults.from_district_id
    this.form.from_address_line = defaults.from_address_line
    this.form.to_province_id = defaults.to_province_id
    this.form.to_district_id = defaults.to_district_id
    this.form.to_address_line = defaults.to_address_line

    this.neighborhoods(this.form.from_district_id).then((neighborhoods) => this.neighborhoodsFrom = neighborhoods)
    this.neighborhoods(this.form.to_district_id).then((neighborhoods) => this.neighborhoodsTo = neighborhoods)

    this.form.demandant_name = this.user.attributes.name
    this.form.demandant_phone = this.user.attributes.phone

    if (this.user.attributes.role_id === 4) {
      this.driver = this.user.attributes.name;
      this.form.driver_id = parseInt(this.user.id);
    }

    this.form.date = this.minDateTime
    this.rules = this.settings('transfer.rules')
  },
  methods: {
    ...mapActions({
      fetchSettings: 'settings/index',
      fetchProvinces: 'provinces/index',
      fetchVehicleTypes: 'vehicleTypes/index',
    }),
    changeFromDistrict () {
      this.form.from_neighborhood_id = null
      this.neighborhoods(this.form.from_district_id).then((neighborhoods) => this.neighborhoodsFrom = neighborhoods)
    },
    changeToDistrict () {
      this.form.to_neighborhood_id = null
      this.neighborhoods(this.form.to_district_id).then((neighborhoods) => this.neighborhoodsTo = neighborhoods)
    },
    async neighborhoods (districtId) {
      if (!districtId) {
        return []
      }

      const { data } = await this.$http.get('districts/' + districtId)
      return data.data.neighborhoods
    },
    loadTransferDetails (transferId) {
      this.showSpinner()

      this.$http
          .get(`transfers/${transferId}`)
          .then(({ data }) => {
            this.form.date = new Date(data.data.transfer_date)
            this.form.from_province_id = data.data.from_province.id
            this.form.from_district_id = data.data.from_district.id
            this.form.from_neighborhood_id = data.data.from_neighborhood.id
            this.form.to_province_id = data.data.to_province.id
            this.form.to_district_id = data.data.to_district.id
            this.form.to_neighborhood_id = data.data.to_neighborhood.id
            this.form.request_unit = data.data.request_unit_specification.id
            this.form.duty_type = data.data.duty_type_specification.id
            this.form.vehicle_type_id = data.data.vehicle_type_id

            this.neighborhoods(data.data.from_district.id).then((neighborhoods) => this.neighborhoodsFrom = neighborhoods)
            this.neighborhoods(data.data.to_district.id).then((neighborhoods) => this.neighborhoodsTo = neighborhoods)

            Object.keys(data.data).filter(key => key in this.form).forEach(key => this.form[key] = data.data[key])
        })
        .finally(() => {
          this.hideSpinner()
        });
    },
    submit () {
      this.isLoading = true

      const transfer_date = moment(this.form.date).format('YYYY-MM-DD HH:mm').toString()
      const params = {...this.form, ...{ transfer_date }}

      this.$http.post('transfers', params).then(response => {
        if (response.status === 200 || response.status === 201) {
          this.$buefy.toast.open({
            message: 'Taşıt görevi oluşturuldu.',
            type: 'is-success',
            duration: 5000,
          })

          this.$router.push('/transfers')
        }
      }).catch(({ response }) => {
        const errors = this.$_.flattenDeep(this.$_.values(response.data.errors)).join('<br>')

        this.$buefy.toast.open({
          message: 'İşlem sırasında hata oluştu:<br>' + errors,
          type: 'is-warning',
          duration: 5000,
        })

        this.isLoading = false
      })
    },
    addPassenger () {
      this.form.passengers.push({ name: null, phone: '+90' });
      setTimeout(() => {
        let passengerNameInputs = document.getElementsByClassName('additional_passenger_name');
        let lastPassengerNameInputs = passengerNameInputs[passengerNameInputs.length - 1];
        lastPassengerNameInputs.focus();
      }, 100)
    },
    deletePassenger (index) {
      this.form.passengers.splice(index, 1);
    },
    focusInput (e) {
      this.$nextTick()
        .then(() => {
          const vals = e.target.value;
          e.target.value = '';
          e.target.value = vals;
      })
    },
    getAsyncDriverData: debounce(function (name) {
      this.drivers = []

      if (!name.length) {
        return
      }

      this.isDriverFetching = true

      this.$http.get('available-drivers', {
        params: {
          transfer_id: null,
          estimated_time: this.form.estimated_time,
          name,
          date: this.form.date
        }
      }).then(({ data }) => {
        this.drivers = data.data
      }).catch(() => {
        this.drivers = []
      }).finally(() => {
        this.isDriverFetching = false
      })
    }, 500),
    getAsyncSecondDriverData: debounce(function (name) {
      this.drivers = []

      if (!name.length) {
        return
      }

      this.isSecondDriverFetching = true

      this.$http.get('available-drivers', {
        params: {
          transfer_id: null,
          estimated_time: this.form.estimated_time,
          name,
        }
      }).then(({ data }) => {
        this.drivers = data.data
      }).catch(() => {
        this.drivers = []
      }).finally(() => {
        this.isSecondDriverFetching = false
      })
    }, 500),
    getAsyncCarData: debounce(function (name) {
      this.cars = []

      if (!name.length) {
        return
      }

      this.isCarFetching = true

      this.$http.get('list-cars', {
        params: {
          transfer_id: null,
          estimated_time: this.form.estimated_time,
          q: name,
          date: this.form.date
        }
      }).then(({ data }) => {
        this.cars = data
      }).catch(() => {
        this.cars = []
      }).finally(() => {
        this.isCarFetching = false
      })
    }, 500),
    selectDriver (option) {
      if (option) {
        const driver_id = option.id;
        this.form.driver_id = driver_id;
        this.driver = option;
        this.drivers = [];

        this.$http.post('check-driver', {
          driver_id: option.id,
          transfer_id: null,
          date: this.form.date
        }).then(r => {
          if (r.data.count > 0) {
            this.$buefy.toast.open({
              message: this.$t('driverWarning', [r.data.count]),
              type: 'is-warning',
              duration: 5000,
              queue: false
            });
          }
        })
      }
    },
    selectSecondDriver (option) {
      if (option) {
        const second_driver_id = option.id;
        this.form.second_driver_id = second_driver_id;
        this.secondDriver = option;
        this.drivers = [];

        this.$http.post('check-driver', {
          driver_id: option.id,
          transfer_id: null,
          date: this.form.date
        }).then(r => {
          if (r.data.count > 0) {
            this.$buefy.toast.open({
              message: this.$t('driverWarning', [r.data.count]),
              type: 'is-warning',
              duration: 5000,
              queue: false
            });
          }
        })
      }
    },
    selectCar (option) {
      if (option) {
        this.form.car_plate = option.plate;
        this.form.car_id = option.id;
        this.cars = [];

        this.$http.post('check-car', {
          plate: option.plate,
          transfer_id: null,
          date: this.form.date
        }).then(r => {
          if (r.data.count > 0) {
            this.$buefy.toast.open({
              message: this.$t('carWarning', [r.data.count]),
              type: 'is-warning',
              duration: 5000,
              queue: false
            });
          }
        })
      }
    },
  }
}
</script>

<style scoped>
  .self-drive-area {
    margin-top:40px;
  }

  .google-image {
    height: 307px;
  }
</style>
